import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';

import { intersection, isEmpty, pick } from 'lodash/fp';

import {
    AGE_GROUP_ID_KEY,
    AGE_MAX_KEY,
    AGE_MIN_KEY,
    CAUSE_ID_KEY,
    METRIC_ID_KEY,
} from '@portal/common/models/data-key';
import { getDataTypePrimaryEntityKey } from '@portal/common/models/data-type';
import { isSingleYearAgeEstimatesExportEnabled as checkIfSingleYearAgeEstimatesExportEnabled } from '@portal/common/models/organization';
import { DataType } from '@portal/common/types';

import config from '../../../config';
import {
    getSelectedConditionsDataTypes,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataProject,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getSingleYearAgeGranularity,
} from '../../../store/data-explorer/selectors';
import { getOrganization } from '../../../store/organization/selectors';
import ExportSingleAgeEstimatesButton from './ExportSingleAgeEstimatesButton';
import ExportSingleAgeEstimatesModal from './ExportSingleAgeEstimatesModal';

const ExportSingleAgeEstimates: FC = () => {
    const selectedDataProject = useSelector(getSelectedDataProject);
    const selectedRefinementFilters = useSelector(
        getSelectedRefinementFiltersWithFallbackToDefaultFilters
    );
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const selectedConditionsDataTypes = useSelector(getSelectedConditionsDataTypes);
    const organization = useSelector(getOrganization);
    const singleYearAgeGranularity = useSelector(getSingleYearAgeGranularity);

    const [isModalVisible, setModalVisibility] = useState(false);

    const isSingleYearAgeEstimatesExportEnabled =
        checkIfSingleYearAgeEstimatesExportEnabled(organization);

    const isDataProjectSupported = config.singleYearAgeDataProjects.includes(selectedDataProject);
    if (
        !isDataProjectSupported ||
        !isSingleYearAgeEstimatesExportEnabled ||
        !selectedConditionsDataTypes ||
        !selectedRefinementFilters
    ) {
        return null;
    }

    const agesRange = {
        min: singleYearAgeGranularity?.[AGE_MIN_KEY],
        max: singleYearAgeGranularity?.[AGE_MAX_KEY],
    };

    if (!agesRange.min || !agesRange.max) {
        return null;
    }

    const unsupportedDataTypes = (selectedConditionsDataTypes || []).filter(
        (type) => !config.singleYearAgeDataTypes.includes(type)
    );
    const isRestrictedDataTypeSelected = !isEmpty(unsupportedDataTypes);

    const filters = {
        ...refinedGranularity,
        ...pick(Object.keys(refinedGranularity), selectedRefinementFilters),
        ...(!isEmpty(refinedGranularity[CAUSE_ID_KEY]) && {
            [CAUSE_ID_KEY]: refinedGranularity[CAUSE_ID_KEY],
        }),
    };

    if (!isEmpty(filters[METRIC_ID_KEY]) && !isEmpty(singleYearAgeGranularity?.[METRIC_ID_KEY])) {
        filters[METRIC_ID_KEY] = intersection(
            filters[METRIC_ID_KEY],
            singleYearAgeGranularity[METRIC_ID_KEY] as number[]
        );
    }

    delete filters[AGE_GROUP_ID_KEY];
    if (isRestrictedDataTypeSelected) {
        unsupportedDataTypes.forEach((type) => {
            const key = getDataTypePrimaryEntityKey(type);
            delete filters[key];
        });
    }

    const couldReportBeGenerated = !Object.entries(filters).find(([key, value]) => {
        const granularity = singleYearAgeGranularity?.[key] ?? [];
        return !isEmpty(granularity) && isEmpty(intersection(granularity, value as number[]));
    });

    return (
        <>
            <ExportSingleAgeEstimatesButton
                selectedConditionsDataTypes={selectedConditionsDataTypes}
                selectedRefinementFilters={selectedRefinementFilters}
                setModalVisibility={setModalVisibility}
                supportedDataTypes={config.singleYearAgeDataTypes as DataType[]}
                disabled={!couldReportBeGenerated}
                selectedFilters={filters}
                granularity={singleYearAgeGranularity}
            />
            {couldReportBeGenerated && (
                <ExportSingleAgeEstimatesModal
                    isVisible={isModalVisible}
                    supportedAgeRange={agesRange}
                    setVisibility={setModalVisibility}
                    supportedDataTypes={config.singleYearAgeDataTypes as DataType[]}
                    selectedFilters={filters}
                    selectedConditionsDataTypes={selectedConditionsDataTypes}
                />
            )}
        </>
    );
};

export default ExportSingleAgeEstimates;
